import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { httpsCallable } from 'firebase/functions';
import {
  LuScanFace,
  LuFlipHorizontal,
  LuSmile,
  LuCamera,
  LuArrowRight,
  LuTrendingUp,
} from 'react-icons/lu';
import { auth, functions } from '../firebase/Init';
import AppLayout from '../components/oracle/AppLayout';
import SegmentedSwitch from '../components/oracle/SegmentedSwitch';
import PageHeader from '../components/oracle/PageHeader';
import LogoSpinner from '../components/oracle/LogoSpinner';
import EmptyState from '../components/oracle/EmptyState';
import ScanRevealModal from '../components/oracle/ScanRevealModal';
import useEntitlement from '../hooks/useEntitlement';
import UnlockGateModal from '../components/oracle/UnlockGateModal';
import ScanViewport from '../components/create/ScanViewport';
import CaptureButton from '../components/create/CaptureButton';
import { GOLD_NUMERAL_STYLE } from '../components/oracle/ScoreNumeral';
import HowItWorks from '../components/create/HowItWorks';
import TipChip from '../components/create/TipChip';
import ScanPhotoPickerModal from '../components/create/ScanPhotoPickerModal';
import ProductUsageStep from '../components/create/ProductUsageStep';
import GlowupSlot, { GlowupSlotFooter } from '../components/create/GlowupSlot';
import ArchiveStrip from '../components/create/ArchiveStrip';
import PublishButton from '../components/create/PublishButton';
import NoFaceRejection from '../components/oracle/rejections/NoFaceRejection';
import MultipleFacesRejection from '../components/oracle/rejections/MultipleFacesRejection';
import Under16Rejection from '../components/oracle/rejections/Under16Rejection';
import VerifyEmailRejection from '../components/oracle/rejections/VerifyEmailRejection';
import NSFWRejection from '../components/oracle/rejections/NSFWRejection';
import NetworkFailRejection from '../components/oracle/rejections/NetworkFailRejection';
import { REJECTION_CODES, ROUTINE_ROUTES } from '../contracts/oracleApi';
import { isListVaultEntriesResponse } from '../contracts/vaultApi';
import { isGlowupCreateResponse } from '../contracts/glowupApi';
import { ROUTES, vaultEntryPath } from '../contracts/routes';
import { COPY } from '../contracts/copyMap';
import { preparePortrait, warmFacePrep } from '../lib/facePrep';
import { scoreAligned, warmAestheticModel } from '../lib/aestheticModel';
import { analyzeGeometry } from '../lib/geometry';
import { formatScore10, formatPercentile, scanDateMs } from '../components/create/format';
import useIsMobile from '../hooks/useIsMobile';
import useScanHistory from '../hooks/useScanHistory';
import { useUser } from '../contexts/UserContext';
import {
  isScanBlocked, hoursUntilQuotaReset, hasOutstandingUnlock, localUnlockProgress,
} from '../lib/scanQuota';
import { computeScoreDelta } from '../lib/scoreDelta';

const MAX_FILE_BYTES = 5 * 1024 * 1024;

// Unified Create surface (design-recreation): NEW SCAN capture + NEW GLOWUP
// composer behind a segmented switch. Mode lives in the query string
// (?mode=glowup) so /create deep-links land on the right builder.
//
// Scan capture state machine (wired to the real Oracle pipeline; CAPTURE
// press semantics — the photo is staged on drop/pick, the pipeline runs on
// the CAPTURE press):
//
//   idle ──drop/pick──▶ preparing ──facePrep──▶ staged ──CAPTURE──▶ analyzing
//     ▲                     │                                            │
//     │                 clientError ◀── bad type / >5MB                  │
//     │                          ┌──────────┬──────────────┬─────────────┼────────────┐
//     └─ CAPTURE NEW SCAN ─ done ┘       locked    rejected/error     QUOTA      SCAN_PENDING
//                          (open)       (sealed)                    EXHAUSTED       _UNLOCK
//                                          │                            │              │
//                             CAPTURE ─────┤        quotaBlocked ◀──────┘              │
//                             /judge?unlock=id         │  ▲                            │
//                                             CAPTURE (manual)│ │ pre-check            │
//                                                      ▼  │                            │
//                                                   analyzing        pendingUnlock ◀───┘
//                                                                       │  ▲
//                                                         CAPTURE ──────┘  │ pre-check
//                                                         /judge
//
// THREE terminal phases now, and none of them auto-advances:
//
//   quotaBlocked  no allowance left today. Exit: a CAPTURE press retries the
//                 server, which is the authority on the UTC day roll.
//
//                 ⚠ UNREACHABLE. The daily cap is off — both allowances are
//                 Infinity, so isScanBlocked is always false and the server
//                 never answers QUOTA_EXHAUSTED. See the header of
//                 functions/oracleFunctions/scanQuota.js: this phase is kept
//                 only because restoring a cap is a two-constant change. If
//                 the cap is not coming back, delete the phase, its status
//                 chip, the pre-checks and the rejection branch together.
//   locked        THIS reading is COMPLETE but sealed behind the unlock gate.
//                 Exit: a CAPTURE press leaves for /judge. It must NOT fall
//                 into the default resetScan() branch — that would burn the
//                 next day's scan on a reading already computed and paid for.
//   pendingUnlock a PREVIOUS reading is still sealed, so this one was refused
//                 before it ran. Exit: a CAPTURE press leaves for /judge.
//
//                 The distinction from `locked` is which scan is sealed, and it
//                 decides the copy: `locked` says "ten scans to open THIS",
//                 which would be false here — nothing was computed. Both render
//                 the same UnlockGateModal because the decision offered (judge,
//                 or start a trial) is identical.
//
//                 Reachable from TWO directions, deliberately: the client
//                 pre-check (so a multi-megabyte portrait is never uploaded to
//                 be refused) and the server's SCAN_PENDING_UNLOCK rejection
//                 (the guarantee, against a doc the client cannot forge).
//
// The EMAIL_UNVERIFIED rejection (server gate OR client pre-check) shows the
// confirm-email gate instead of a content-rejection card; once the user
// confirms, onVerified re-runs the staged scan.
//
// quotaBlocked is TERMINAL and has exactly ONE exit: a user-initiated CAPTURE
// press. Nothing auto-advances out of it.
//
// That is not incidental. When the client's quota view disagreed with the
// server's (stale onSnapshot data, device clock skew), re-entering 'staged'
// let the auto-run effects re-fire the pipeline in an unbounded ~10 calls/sec
// loop — the 07/16 incident. The offerwall-era code kept a reactive resume
// alive behind an at-rejection snapshot comparison; with the offerwall gone,
// that effect was deleted outright and no automatic resume replaces it. A
// blocked user's quota reopens at UTC midnight, which does not write the user
// doc, so there was never a reactive signal to wait for anyway.
function rejectionView(rejection, onRetry, onVerified) {
  switch (rejection.rejection_code) {
    case REJECTION_CODES.NO_FACE:
      return <NoFaceRejection onRetry={onRetry} />;
    case REJECTION_CODES.EMAIL_UNVERIFIED:
      return <VerifyEmailRejection onVerified={onVerified} />;
    case REJECTION_CODES.MULTIPLE_FACES:
      return <MultipleFacesRejection onRetry={onRetry} />;
    case REJECTION_CODES.UNDER_16:
      return <Under16Rejection />;
    case REJECTION_CODES.NSFW:
    case REJECTION_CODES.PHOTODNA_MATCH:
      return <NSFWRejection onRetry={onRetry} />;
    case REJECTION_CODES.MODEL_REFUSED:
    case REJECTION_CODES.MODEL_MALFORMED:
    case REJECTION_CODES.INTERNAL:
    default:
      return <NetworkFailRejection message={rejection.rejection_message} onRetry={onRetry} />;
  }
}

function scanStatusFor(scan) {
  switch (scan.phase) {
    case 'preparing':
      return { text: 'PREPARING PORTRAIT…', color: '#E8E2D6' };
    case 'staged':
      return { text: 'PORTRAIT STAGED · CAPTURE WHEN READY', color: '#E8E2D6' };
    case 'usageCheck':
      // Not "BEFORE WE READ" — that duplicated the card's own header verbatim,
      // and not the 'staged' copy either: it ends "CAPTURE WHEN READY" and the
      // capture button is deliberately not rendered during this step.
      return { text: 'PORTRAIT STAGED · ANSWER TO CONTINUE', color: '#E8E2D6' };
    case 'locked':
      return { text: COPY.UNLOCK_GATE_SEALED_STATUS, color: '#E8E2D6' };
    case 'pendingUnlock':
      return { text: COPY.SCAN_PENDING_UNLOCK_STATUS, color: '#E8E2D6' };
    case 'quotaBlocked':
      // No "UNLOCK TO CONTINUE" any more — the offerwall that sold extra scans
      // is gone, so the only honest instruction is when it reopens.
      return {
        text: `DAILY LIMIT REACHED · REOPENS IN ${hoursUntilQuotaReset()}H`,
        color: '#E8E2D6',
      };
    case 'analyzing':
      return { text: 'ANALYZING 32 FACIAL VECTORS…', color: '#C9A45C' };
    case 'done':
      return {
        text: `SCAN COMPLETE · ${formatScore10(scan.result.verdict.overall_score)} / 10`,
        color: '#E6C083',
      };
    case 'rejected':
      return { text: 'SUBMISSION DECLINED', color: '#8A857C' };
    case 'error':
    case 'clientError':
      return { text: 'SUBMISSION INTERRUPTED', color: '#8A857C' };
    default:
      return { text: 'SUBMIT PORTRAIT FOR ANALYSIS', color: '#E8E2D6' };
  }
}

// Same gold as the scan-detail score numeral — one definition, in ScoreNumeral.
const DELTA_GRADIENT_STYLE = GOLD_NUMERAL_STYLE;

const Create = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { userData } = useUser() || {};
  const { isPro } = useEntitlement();
  // Latest user doc read inside the stable runPipeline callback without making
  // it (and the mobile auto-run effect that depends on it) churn on every snapshot.
  const userDataRef = useRef(userData);
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);
  // Same trick for membership: the daily allowance depends on it, and runPipeline
  // must stay a stable callback.
  const isProRef = useRef(isPro);
  useEffect(() => {
    isProRef.current = isPro;
  }, [isPro]);
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get('mode') === 'glowup' ? 'glowup' : 'scan';
  const setMode = useCallback(
    (key) => setSearchParams(key === 'glowup' ? { mode: 'glowup' } : {}, { replace: true }),
    [setSearchParams],
  );

  // Prior scan history, loaded once on mount. One job now: the reveal delta
  // below. It also fed a returning-member summary here, which has moved to
  // /home — this is the scanner, and it shows the same steps to everyone.
  //
  // priorLatestScore is captured in a ref the moment history first resolves and
  // then left alone — after a successful scan the newest entry IS this scan, so
  // reading `latest` at reveal time would compare the score against itself.
  const scanHistory = useScanHistory();
  const priorLatestScoreRef = useRef(null);
  const priorCapturedRef = useRef(false);
  useEffect(() => {
    if (priorCapturedRef.current || scanHistory.phase !== 'ready') return;
    priorCapturedRef.current = true;
    priorLatestScoreRef.current = scanHistory.latest
      ? Number(scanHistory.latest.overall_score)
      : null;
  }, [scanHistory.phase, scanHistory.latest]);

  // ── Scan capture state ─────────────────────────────────────────────────
  const [scan, setScan] = useState({ phase: 'idle' });
  // Scan-complete reveal modal — re-armed on every pipeline run, dismissible.
  const [revealDismissed, setRevealDismissed] = useState(false);
  // Mobile-only: the "select a photo" bottom sheet (posing prompts + picker).
  const [pickerOpen, setPickerOpen] = useState(false);
  // Actives eligible for the "did you use these?" step, or null when the step
  // does not apply. Prefetched in the background rather than fetched on the
  // CAPTURE press, which would add a round trip to the hottest action on the
  // page — and only for users who could actually see it.
  const [usageActives, setUsageActives] = useState(null);

  useEffect(() => {
    warmFacePrep();
    warmAestheticModel();
  }, []);

  // Eligibility for the reported-use step: a member, with a routine that has
  // product actives, on a second or later reading. A first scan has nothing to
  // report against.
  //
  // READ THE PAYLOAD SHAPE FROM getRoutine, NOT FROM A `routine` WRAPPER.
  // getRoutine returns { status, am, pm, total_price, stage } at the TOP level;
  // it has never returned a `routine` key. Reading `data.routine.actives` made
  // `actives` unconditionally empty, so this always stored null and the step
  // was unreachable for everyone — the scan never asked the routine questions.
  //
  // Advisory cards (clinician / behavioral) are filtered out: they carry no
  // product, so "have you been using this?" has nothing to point at.
  //
  // No efficacy-tracking condition — tracking is always on now (see Routine.js).
  useEffect(() => {
    if (!isPro || !scanHistory.hasScans) { setUsageActives(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const getRoutine = httpsCallable(functions, 'getRoutine');
        const res = await getRoutine({});
        const data = res?.data;
        if (cancelled || data?.status !== 'success') return;
        const actives = [...(data.am || []), ...(data.pm || [])]
          .filter((a) => a && a.active_id && (!a.route || a.route === ROUTINE_ROUTES.PRODUCT));
        setUsageActives(actives.length ? actives : null);
      } catch (err) {
        // Non-fatal: without it the step simply does not appear.
        console.warn('Create: routine prefetch failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isPro, scanHistory.hasScans]);

  // Revoke the staged preview object URL when replaced or on unmount.
  useEffect(() => {
    const url = scan.prep && scan.prep.previewUrl;
    if (!url || typeof url !== 'string' || !url.startsWith('blob:')) return undefined;
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        /* no-op */
      }
    };
  }, [scan.prep]);

  const resetScan = useCallback(() => setScan({ phase: 'idle' }), []);

  // Backstop against retry loops: at most 5 server pipeline calls per staged
  // portrait per rolling 60s. quotaBlocked already makes the known loop shape
  // structurally impossible; this guard is insurance against any FUTURE effect
  // that auto-re-runs a rejection phase — an unbounded loop past the quota
  // gate would hit the expensive pipeline stages, not the cheap rejection.
  const attemptGuardRef = useRef({ prep: null, times: [] });
  // Concurrency guard: the mobile auto-run and reactive-unlock effects can
  // both observe 'staged' in the same commit (e.g. a credit lands while a
  // scan is parked on mobile) and would otherwise issue two concurrent
  // server calls — the second burns the fresh quota as a rejection and its
  // late resolution can overwrite a successful (charged) verdict.
  const inFlightRef = useRef(false);

  const handleDrop = useCallback(async (files) => {
    const file = files && files[0];
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      setScan({
        phase: 'clientError',
        headline: COPY.UPLOAD_TYPE_ERROR_HEADLINE,
        subtitle: COPY.UPLOAD_TYPE_ERROR_SUBTITLE,
      });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setScan({
        phase: 'clientError',
        headline: COPY.UPLOAD_SIZE_ERROR_HEADLINE,
        subtitle: COPY.UPLOAD_SIZE_ERROR_SUBTITLE,
      });
      return;
    }
    setScan({ phase: 'preparing' });
    try {
      const prep = await preparePortrait(file);
      setScan({ phase: 'staged', prep });
    } catch (err) {
      setScan({
        phase: 'clientError',
        headline: COPY.UPLOAD_TYPE_ERROR_HEADLINE,
        subtitle: COPY.UPLOAD_TYPE_ERROR_SUBTITLE,
      });
    }
  }, []);

  const runPipeline = useCallback(async (prep, productUsage) => {
    // Client-side verified-email pre-check (UX only — runOraclePipeline
    // re-enforces it). Unverified → park on the confirm-email gate with the
    // portrait retained so I'VE CONFIRMED can resume the scan.
    const authUser = auth.currentUser;
    if (authUser && authUser.email && !authUser.emailVerified) {
      setScan({
        phase: 'rejected',
        prep,
        rejection: {
          status: 'rejected',
          rejection_code: REJECTION_CODES.EMAIL_UNVERIFIED,
          rejection_message: COPY.VERIFY_EMAIL_SUBTITLE,
        },
      });
      return;
    }
    // Client-side quota pre-check (UX only — runOraclePipeline re-enforces it).
    // Blocked → park TERMINALLY in quotaBlocked, keeping the prep so a manual
    // CAPTURE can retry against the server once the day rolls.
    //
    // This used to park in 'staged' and open the offerwall modal, so a landing
    // credit could auto-resume it. With the offerwall gone there is nothing to
    // wait for, and parking in 'staged' would be actively dangerous: the mobile
    // auto-run effect treats 'staged' as "go" and would re-fire immediately.
    if (isScanBlocked(userDataRef.current, { isPro: isProRef.current })) {
      setScan({ phase: 'quotaBlocked', prep });
      return;
    }
    // Unlock-debt pre-check. Same posture as the quota one above: affordance
    // only, re-enforced at runOraclePipeline step 2.6. Worth doing client-side
    // because the request body is a base64 portrait — several megabytes shipped
    // to a server whose first act would be to refuse it.
    //
    // No `unlock` block here: the client is inferring the refusal rather than
    // being told, so the modal draws from `userData` instead.
    if (hasOutstandingUnlock(userDataRef.current, { isPro: isProRef.current })) {
      setScan({ phase: 'pendingUnlock', prep, unlock: localUnlockProgress(userDataRef.current) });
      return;
    }
    // Drop duplicate concurrent invocations (see inFlightRef above) without
    // burning an attempt.
    if (inFlightRef.current) return;
    // Attempt-cap backstop (counts only real server attempts — pre-check
    // returns above don't reach here).
    const guard = attemptGuardRef.current;
    if (guard.prep !== prep) {
      guard.prep = prep;
      guard.times = [];
    }
    const now = Date.now();
    guard.times = guard.times.filter((t) => now - t < 60_000);
    if (guard.times.length >= 5) {
      setScan({ phase: 'error', prep, message: COPY.REJECT_RATE_LIMIT_SUBTITLE });
      return;
    }
    guard.times.push(now);
    inFlightRef.current = true;
    setRevealDismissed(false);
    setScan({ phase: 'analyzing', prep });
    try {
      // Stage 4: client-side learned aesthetic score on the aligned crop. Runs
      // only on a facePrep-normalized portrait; null-safe (a null result just
      // means no percentile — the Gemini verdict still renders). Computed
      // before the callable so it persists atomically with the scan record.
      const client =
        prep.normalized && prep.alignedCanvas
          ? await scoreAligned(prep.alignedCanvas).catch(() => null)
          : null;
      // Stage 2: deterministic 2D geometry from the facePrep landmarks (pure,
      // synchronous, main-thread-cheap). null when no landmarks (passthrough).
      const geometry = prep.normalized ? analyzeGeometry(prep.landmarks) : null;
      const runOracle = httpsCallable(functions, 'runOraclePipeline');
      const result = await runOracle({
        imageBase64: prep.imageBase64,
        mimeType: prep.mimeType,
        client_learned_raw: client ? client.raw : null,
        client_learned_percentile: client ? client.percentile : null,
        client_geometry: geometry,
        // Reported product use, collected by ProductUsageStep immediately above.
        // OMITTED, never sent as [], when the user skipped: the server treats an
        // absent key as "declined to answer" and an empty array the same way,
        // but sending nothing keeps the two indistinguishable at the wire, which
        // is what SKIP promises. runOraclePipeline re-sanitizes it against the
        // caller's own routine regardless.
        ...(productUsage && productUsage.length ? { product_usage: productUsage } : {}),
      });
      const data = result && result.data;
      if (data && data.status === 'success' && data.unlock && data.unlock.state === 'locked') {
        // The reading is complete and SEALED. TERMINAL, like quotaBlocked and
        // for the same reason: the mobile auto-run effect treats 'staged' as
        // "go", so anything that re-enters it loops. Nothing auto-advances out
        // of here — see the note where the reactive-unlock effect used to be.
        //
        // The payload carries no verdict at all, so there is nothing to render
        // and nothing to leak.
        setScan({ phase: 'locked', prep, scanId: data.scan_id, unlock: data.unlock });
      } else if (data && data.status === 'success') {
        setScan({ phase: 'done', prep, result: data, client });
      } else if (data && data.rejection_code === REJECTION_CODES.QUOTA_EXHAUSTED) {
        // Server said no — our local quota view is wrong or stale (second-tab
        // race, dead onSnapshot listener, device clock skew). TERMINAL: do not
        // re-enter 'staged' (the auto-run effects treat 'staged' as "go" and
        // would loop — that was the 07/16 incident).
        //
        // The `quotaAtBlock` snapshot this used to carry is gone with the
        // reactive-unlock effect that consumed it: nothing auto-resumes from
        // here any more, so there is no "has the doc moved?" comparison left to
        // make. Recovery is the user pressing CAPTURE, handled below.
        setScan({ phase: 'quotaBlocked', prep });
      } else if (data && data.rejection_code === REJECTION_CODES.SCAN_PENDING_UNLOCK) {
        // A previous reading is still sealed. Scans are unlimited, but only one
        // may be outstanding — the server refuses before spending anything.
        //
        // TERMINAL for the same reason as quotaBlocked: the mobile auto-run
        // effect treats 'staged' as "go", so re-entering it would loop against
        // a server that will keep refusing. The only exit is a CAPTURE press,
        // which leaves for /judge rather than retrying.
        setScan({ phase: 'pendingUnlock', prep, unlock: data.unlock || null });
      } else if (data && data.status === 'rejected') {
        setScan({ phase: 'rejected', prep, rejection: data });
      } else {
        setScan({ phase: 'error', prep, message: COPY.REJECT_NETWORK_SUBTITLE });
      }
    } catch (err) {
      setScan({
        phase: 'error',
        prep,
        message: (err && err.message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const dropzoneEnabled = scan.phase === 'idle';
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    disabled: !dropzoneEnabled,
    multiple: false,
    noClick: !dropzoneEnabled,
    noKeyboard: !dropzoneEnabled,
  });

  // Mobile is single-tap (Instagram-like): once a portrait stages, run the
  // pipeline automatically instead of waiting for a second CAPTURE press, and
  // dismiss the picker sheet the moment we leave idle.
  useEffect(() => {
    if (!isMobile) return;
    if (scan.phase !== 'idle' && pickerOpen) setPickerOpen(false);
    if (scan.phase !== 'staged') return;
    // The reported-use step interrupts the mobile single-tap flow on purpose:
    // it is the only moment the answers can be collected, and auto-running
    // past it would silently discard them.
    if (usageActives) setScan({ phase: 'usageCheck', prep: scan.prep });
    else runPipeline(scan.prep);
  }, [isMobile, scan.phase, scan.prep, pickerOpen, runPipeline, usageActives]);

  // NOTE: the reactive-unlock effect that used to live here is GONE, deleted
  // with the offerwall.
  //
  // It watched `userData` for a landing scan credit and auto-resumed a parked
  // scan. It was the only effect in this file that ever advanced OUT of a
  // blocked phase, and it needed an at-rejection snapshot comparison to avoid
  // becoming the 07/16 unbounded retry loop.
  //
  // Nothing replaces it, because there is nothing left to wait for: a blocked
  // user's quota reopens at UTC midnight, which does not write the user doc and
  // so could never have triggered a reactive resume anyway. Recovery is a
  // deliberate CAPTURE press, handled below.
  //
  // Do not reintroduce an auto-advance out of `quotaBlocked`.

  const handleCapture = () => {
    switch (scan.phase) {
      case 'idle':
        // Out of quota, or a previous reading still sealed → say so before
        // picking a photo, rather than staging a portrait only to bounce off
        // the pre-check.
        if (isScanBlocked(userDataRef.current, { isPro: isProRef.current })) {
          setScan({ phase: 'quotaBlocked' });
          return;
        }
        if (hasOutstandingUnlock(userDataRef.current, { isPro: isProRef.current })) {
          setScan({ phase: 'pendingUnlock', unlock: localUnlockProgress(userDataRef.current) });
          return;
        }
        // Mobile opens the photo-picker sheet first (posing prompts live
        // there); desktop fires the native picker directly.
        if (isMobile) setPickerOpen(true);
        else open();
        return;
      case 'staged':
        // Reported use is collected BEFORE the pipeline runs — it is a single
        // awaited callable, so the answers have to ride in the request.
        if (usageActives) {
          setScan({ phase: 'usageCheck', prep: scan.prep });
          return;
        }
        runPipeline(scan.prep);
        return;
      // Both terminal "you already have a sealed reading" phases exit the same
      // way: to the deck. Neither may fall through to the default resetScan()
      // branch, which would discard a reading already computed and paid for.
      //
      // This case was previously two lines copy-pasted out of scanStatusFor —
      // it returned a { text, color } object from a void handler, so pressing
      // CAPTURE on a sealed scan did nothing at all.
      case 'locked':
        navigate(`${ROUTES.JUDGE}?unlock=${scan.scanId}`);
        return;
      case 'pendingUnlock':
        // No scan id to carry: the sealed scan is a PREVIOUS one, and the deck
        // resolves which of the caller's scans is owed anyway.
        navigate(ROUTES.JUDGE);
        return;
      case 'quotaBlocked':
        // Parked out of quota. A user-initiated press retries against the
        // server — the authority — because the quota may have genuinely
        // reopened without the user doc changing (the UTC day rolls without a
        // doc write; device clock skew resolves; a membership just started).
        // If still blocked the rejection re-parks here, and the 5-per-60s
        // attempt cap bounds any hammering.
        //
        // This is the ONLY path out of quotaBlocked, and it is user-initiated
        // by design — see the deleted reactive-unlock note above.
        //
        // With no prep (blocked before choosing a photo) there is nothing to
        // resubmit, so fall back to opening the picker.
        if (scan.prep) runPipeline(scan.prep);
        else if (isMobile) setPickerOpen(true);
        else open();
        return;
      case 'preparing':
      case 'analyzing':
        return;
      case 'usageCheck':
        // NO-OP, and deliberately not the default. The reported-use step owns
        // its own actions (CONFIRM AND ANALYZE / SKIP THIS), so the page-level
        // capture button is not rendered during it — but falling through to
        // `default` would DISCARD the staged portrait, which is what used to
        // happen. Anyone re-adding a capture control here should have to think
        // about that rather than inherit a reset.
        return;
      default:
        // done / rejected / error / clientError → start over
        resetScan();
    }
  };

  // The reported-use step supplies its own primary action, so the page-level
  // CAPTURE SCAN is suppressed while it is up — see the desktop card below.
  const isUsageStep = scan.phase === 'usageCheck';

  const captureBtnState =
    scan.phase === 'preparing' || scan.phase === 'analyzing'
      ? 'scanning'
      : scan.phase === 'done'
        ? 'done'
        : 'idle';

  const scanIssue =
    scan.phase === 'rejected' || scan.phase === 'error'
    || scan.phase === 'clientError' || scan.phase === 'usageCheck';
  const status = scanStatusFor(scan);

  // Shared ScanViewport content (desktop inline frame + mobile fullscreen
  // overlay render the same staged photo / mesh / done state from here).
  const scanViewportProps = {
    phase: scan.phase,
    previewUrl: scan.prep ? scan.prep.previewUrl : null,
    landmarks: scan.prep ? scan.prep.landmarks : null,
    meshEdges: scan.prep ? scan.prep.meshEdges : null,
    score10:
      scan.phase === 'done' ? formatScore10(scan.result.verdict.overall_score) : null,
    percentile:
      scan.phase === 'done'
        ? formatPercentile(
            // Prefer the locally-computed score (always present when the model
            // ran); fall back to the backend echo.
            scan.client && typeof scan.client.percentile === 'number'
              ? scan.client.percentile
              : scan.result.learned_percentile,
          )
        : null,
    statusText: status.text,
    statusColor: status.color,
  };

  // Shared rejection / error / interstitial view (desktop info card + mobile
  // fullscreen overlay). The reported-use step rides the same slot because it
  // occupies the same moment — the scan is staged, and something has to be
  // answered before the pipeline runs.
  const scanIssueView = (
    <>
      {scan.phase === 'usageCheck' && (
        <ProductUsageStep
          actives={usageActives || []}
          onConfirm={(entries) => runPipeline(scan.prep, entries)}
          onSkip={() => runPipeline(scan.prep)}
        />
      )}
      {scan.phase === 'rejected' &&
        rejectionView(scan.rejection, resetScan, () =>
          scan.prep ? runPipeline(scan.prep) : resetScan(),
        )}
      {scan.phase === 'clientError' && (
        <EmptyState
          headline={scan.headline}
          subtitle={scan.subtitle}
          ctaLabel={COPY.TRY_AGAIN_CTA}
          onCta={resetScan}
        />
      )}
      {scan.phase === 'error' && (
        <NetworkFailRejection message={scan.message} onRetry={resetScan} />
      )}
    </>
  );

  // ── Scan-complete reveal modal props ────────────────────────────────────
  //
  // The delta compares against the score that was the user's latest BEFORE this
  // submission — captured on mount, since after a successful scan the new entry
  // is itself the latest and may not have finished indexing anyway.
  //
  // This line has existed in ScanRevealModal since it was written and was passed
  // `null` here, so it has never rendered for a single user. Movement between
  // scans is the only reason the product gives anyone to come back: 1.16
  // sessions per user, 6.8% scan-to-second-scan.
  // Computed ONCE per completed scan and held in state, not derived during
  // render. Two reasons: the baseline has to advance after each scan (a user
  // with unlock credits can scan twice in a session, and the second delta must
  // compare against the first scan, not against the pre-session score), and
  // advancing a ref during render would change what is already on screen.
  const [scanDelta, setScanDelta] = useState(null);
  useEffect(() => {
    if (scan.phase !== 'done') return;
    const current = Number(scan.result.verdict.overall_score);
    const previous = priorLatestScoreRef.current;
    // null previous = first-ever scan; there is nothing to compare against.
    setScanDelta(previous == null ? null : computeScoreDelta(current, previous));
    // This scan is the new baseline for any further scan this session.
    priorLatestScoreRef.current = current;
    priorCapturedRef.current = true;
  }, [scan.phase, scan.result]);

  const revealOpen = scan.phase === 'done' && !revealDismissed;
  const revealScore10 =
    scan.phase === 'done' ? Number(scan.result.verdict.overall_score) / 10 : 0;
  const revealPercentileText = (() => {
    if (scan.phase !== 'done') return null;
    const p =
      scan.client && typeof scan.client.percentile === 'number'
        ? scan.client.percentile
        : scan.result.learned_percentile;
    if (typeof p !== 'number' || !Number.isFinite(p)) return null;
    const top = Math.min(99, Math.max(1, Math.round((1 - p) * 100)));
    return `Top ${top}% of users`;
  })();

  // ── Glowup composer state ──────────────────────────────────────────────
  const [archive, setArchive] = useState({ phase: 'idle' });
  const [slots, setSlots] = useState({ before: null, after: null, active: 'before' });
  const [publish, setPublish] = useState({ phase: 'idle', error: null });
  // Fresh manual uploads per glowup slot — overrides archive selection for that side.
  // { scan_id (sentinel), previewUrl (data URL), imageBase64, mimeType, taken_at: null|iso }
  const [freshUploads, setFreshUploads] = useState({ before: null, after: null });
  const redirectTimer = useRef(null);
  const mountedRef = useRef(true);
  const glowupUploadRef = useRef(null);
  // Which slot a pending manual upload targets (set just before opening the picker).
  const pendingUploadSide = useRef('before');

  useEffect(() => {
    // Re-arm on mount (not just at ref init) so StrictMode's simulated
    // unmount/remount in dev doesn't leave the ref permanently false and
    // strand the archive fetch in its loading phase.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    };
  }, []);

  // NOTE: this effect re-runs when archive.phase flips idle → loading, so the
  // fetch must only be guarded by the unmount ref — a cleanup-scoped cancel
  // flag would abort the in-flight request on that same re-run.
  useEffect(() => {
    if (mode !== 'glowup' || archive.phase !== 'idle') return;
    setArchive({ phase: 'loading' });
    (async () => {
      try {
        const list = httpsCallable(functions, 'listVaultEntries');
        const result = await list({});
        const data = result && result.data;
        if (!mountedRef.current) return;
        if (isListVaultEntriesResponse(data)) {
          setArchive({ phase: 'ready', entries: data.entries });
        } else {
          setArchive({
            phase: 'error',
            message: (data && data.error_message) || COPY.REJECT_NETWORK_SUBTITLE,
          });
        }
      } catch (err) {
        if (mountedRef.current) {
          setArchive({
            phase: 'error',
            message: (err && err.message) || COPY.REJECT_NETWORK_SUBTITLE,
          });
        }
      }
    })();
  }, [mode, archive.phase]);

  const entries = archive.phase === 'ready' ? archive.entries : [];
  const beforeEntry = entries.find((e) => e.scan_id === slots.before) || null;
  const afterEntry = entries.find((e) => e.scan_id === slots.after) || null;
  const beforeFresh = freshUploads.before;
  const afterFresh = freshUploads.after;
  const bothFilled = Boolean((beforeEntry || beforeFresh) && (afterEntry || afterFresh));

  // Picking fills the active slot, then auto-advances before → after.
  // Selecting from archive clears any fresh upload on that same side.
  const handlePick = (entry) => {
    if (publish.phase === 'published' || publish.phase === 'submitting') return;
    setPublish((p) => (p.error ? { ...p, error: null } : p));
    const side = slots.active;
    setSlots((s) =>
      s.active === 'before'
        ? { before: entry.scan_id, after: s.after, active: 'after' }
        : { ...s, after: entry.scan_id },
    );
    setFreshUploads((f) => ({ ...f, [side]: null }));
  };

  const deltaLabel =
    bothFilled && beforeEntry && afterEntry && !beforeFresh && !afterFresh
      ? (() => {
          const d = (afterEntry.overall_score - beforeEntry.overall_score) / 10;
          return `${d >= 0 ? '+' : ''}${d.toFixed(1)}`;
        })()
      : '—';

  const handlePublish = async () => {
    if (!bothFilled || publish.phase !== 'idle') return;
    const beforeMs = beforeFresh
      ? (beforeFresh.taken_at ? Date.parse(beforeFresh.taken_at) : null)
      : scanDateMs(beforeEntry);
    const afterMs = afterFresh
      ? (afterFresh.taken_at ? Date.parse(afterFresh.taken_at) : null)
      : scanDateMs(afterEntry);
    if (beforeMs != null && afterMs != null && afterMs <= beforeMs) {
      setPublish({
        phase: 'idle',
        error: 'THE AFTER SCAN MUST POSTDATE THE BEFORE SCAN.',
      });
      return;
    }
    setPublish({ phase: 'submitting', error: null });
    try {
      const createGlowup = httpsCallable(functions, 'createGlowup');
      const toGlowupSide = (entry, fresh) =>
        fresh
          ? { kind: 'fresh', imageBase64: fresh.imageBase64, mimeType: fresh.mimeType, taken_at: fresh.taken_at || null }
          : { kind: 'scan', scan_id: entry.scan_id };
      const result = await createGlowup({
        before: toGlowupSide(beforeEntry, beforeFresh),
        after: toGlowupSide(afterEntry, afterFresh),
      });
      const data = result && result.data;
      if (isGlowupCreateResponse(data)) {
        setPublish({ phase: 'published', error: null });
        redirectTimer.current = setTimeout(
          () => navigate(`${ROUTES.VAULT}?tab=glowups`),
          1200,
        );
        return;
      }
      if (data && data.status === 'error') {
        setPublish({
          phase: 'idle',
          error: data.error_message || COPY.REJECT_NETWORK_SUBTITLE,
        });
        return;
      }
      setPublish({ phase: 'idle', error: COPY.REJECT_NETWORK_SUBTITLE });
    } catch (err) {
      setPublish({
        phase: 'idle',
        error: (err && err.message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    }
  };

  // Open the file picker for a specific slot. Records the target side so the
  // onChange handler knows where the upload lands, and activates that slot.
  const triggerFreshUpload = useCallback((side) => {
    pendingUploadSide.current = side;
    setSlots((s) => ({ ...s, active: side }));
    if (glowupUploadRef.current) glowupUploadRef.current.click();
  }, []);

  // Upload a photo directly into the targeted glowup slot (kind: 'fresh'). Reads
  // the file as a base64 data URL and stores it in freshUploads state for that
  // side. Selecting from the archive later clears the fresh upload.
  const handleFreshUpload = useCallback((files) => {
    const file = files && files[0];
    if (!file || !file.type.startsWith('image/') || file.size > MAX_FILE_BYTES) return;
    const side = pendingUploadSide.current;
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setFreshUploads((f) => ({
        ...f,
        [side]: {
          scan_id: `__fresh_${side}__`,
          previewUrl: dataUrl,
          imageBase64: dataUrl.split(',')[1],
          mimeType: file.type,
          taken_at: null,
        },
      }));
      setSlots((s) => ({ ...s, [side]: null }));
      setPublish((p) => (p.error ? { ...p, error: null } : p));
    };
    reader.readAsDataURL(file);
  }, []);

  // Set the capture date for a manually uploaded photo. Updates local state
  // only (no Firebase call) since the image has not been scanned yet.
  const handleSetDate = useCallback((scanId, iso) => {
    const side = scanId === '__fresh_before__' ? 'before' : 'after';
    setFreshUploads((f) => ({ ...f, [side]: { ...f[side], taken_at: iso } }));
    setPublish((p) => (p.error ? { ...p, error: null } : p));
    return { ok: true };
  }, []);

  const publishState =
    publish.phase === 'published'
      ? 'published'
      : publish.phase === 'submitting'
        ? 'submitting'
        : bothFilled
          ? 'ready'
          : 'disabled';

  const publishError = publish.error && (
    <p className="font-display italic text-[13px] text-bone-dim text-center mt-3 m-0">
      {publish.error}
    </p>
  );

  // Scan mode is a camera-like fixed screen: lock both wrappers to the viewport
  // so the capture frame fills exactly the space under the header. Glowup mode
  // is taller (slots + archive + publish) — on mobile it must be allowed to
  // grow so the document scroller reaches the publish button instead of letting
  // it spill under the fixed bottom tab bar; on desktop it stays bounded (the
  // slots are width-capped below so the composer fits without scrolling).
  const heightCage = mode === 'scan' ? 'flex-1 min-h-0' : 'flex-1 lg:min-h-0';

  // ── Render ─────────────────────────────────────────────────────────────
  const desktopSwitchOptions = [
    { key: 'scan', label: 'NEW SCAN', icon: <LuScanFace size={16} /> },
    { key: 'glowup', label: 'NEW GLOWUP', icon: <LuFlipHorizontal size={16} /> },
  ];
  const mobileSwitchOptions = [
    { key: 'scan', label: 'SCAN' },
    { key: 'glowup', label: 'GLOWUP' },
  ];

  return (
    <AppLayout
      fitViewport
      header={(
        <PageHeader
          title="CREATE"
          subtitle="Capture a new scan, or compose a glowup from your archive."
          mobileSubtitle="Capture a scan or compose a glowup."
          desktopRight={(
            <SegmentedSwitch
              className="mt-1.5"
              options={desktopSwitchOptions}
              active={mode}
              onChange={setMode}
            />
          )}
          // underline tab row, matching the Vault mobile header
          mobileExtra={(
            <div
              className="flex mt-4 -mx-5 border-b border-hairline-soft"
              role="tablist"
              aria-label="create modes"
            >
              {mobileSwitchOptions.map((opt) => {
                const isActive = opt.key === mode;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setMode(opt.key)}
                    className={[
                      'flex-1 text-center py-3 bg-transparent cursor-pointer',
                      'font-display uppercase tracking-[0.22em] text-[13px]',
                      'border-0 border-b-2 transition-colors duration-200 ease-mogged',
                      isActive
                        ? 'text-aurum-bright border-aurum'
                        : 'text-bone-dim border-transparent',
                    ].join(' ')}
                    style={{ borderBottomStyle: 'solid' }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        />
      )}
    >
      <div className={`px-5 lg:px-12 pb-3 lg:pb-14 flex flex-col ${heightCage}`}>
        <div className={`flex flex-col ${heightCage}`}>
          {mode === 'scan' ? (
            isMobile ? (
            /* ---------- MOBILE SCAN ----------
               One content region under the header / above the bottom tab bar.
               idle → intro (how it works + capture); in-flight → the capture
               window fills the region (mesh while analyzing); issue → centered
               rejection. The reveal modal layers on top once done. */
            <div className="mt-3 flex-1 min-h-0 flex flex-col">
              {scan.phase === 'idle' ? (
                <>
                  <HowItWorks className="flex-1 min-h-0 overflow-y-auto" />
                  <div className="shrink-0 pt-4">
                    <CaptureButton state="idle" onClick={handleCapture} />
                    <p className="font-display text-center text-xs text-bone-dim mt-3 m-0">
                      Your scan is stored privately in your Vault.
                    </p>
                  </div>
                </>
              ) : scanIssue ? (
                // flex-col + justify-center, NOT items-center. The rejection
                // and error views are short and stay vertically centred; the
                // reported-use step declares flex-1 and fills instead, so its
                // list can scroll under a pinned CONFIRM. `items-center` also
                // shrink-wrapped the child, which is what let the usage step
                // grow wider than the phone and centre itself off-screen.
                <div className="flex-1 min-h-0 flex flex-col justify-center">
                  {scanIssueView}
                </div>
              ) : (
                // -mx-5 -mb-3 cancels the content gutters so the capture window
                // spans the region edge-to-edge (header + tab bar still show).
                <div className="flex-1 min-h-0 -mx-5 -mb-3 overflow-hidden">
                  <ScanViewport {...scanViewportProps} fill isDragActive={false} />
                </div>
              )}
            </div>
            ) : (
            /* ---------- SCAN CAPTURE (desktop) ---------- */
            <div className="mt-3 lg:mt-[34px] flex-1 min-h-0 flex flex-col lg:flex-row gap-2.5 lg:gap-9 lg:items-start">
              <div className="w-full lg:w-[440px] flex-1 min-h-0 lg:flex-none flex flex-col items-center gap-2.5 lg:gap-3">
                <ScanViewport
                  {...scanViewportProps}
                  rootProps={getRootProps()}
                  inputProps={getInputProps()}
                  isDragActive={isDragActive}
                />
                {scan.phase === 'done' && (
                  <Link
                    to={vaultEntryPath(scan.result.scan_id)}
                    className="font-display uppercase text-xs tracking-[0.22em] text-bone-dim hover:text-aurum-bright transition-colors duration-200 no-underline"
                  >
                    VIEW IN VAULT →
                  </Link>
                )}
              </div>

              <div className="lg:flex-1 min-w-0 flex flex-col bg-[#1B1B1A] border border-hairline-soft rounded-card p-[18px] lg:p-[26px] lg:h-[587px]">
                {scanIssue ? (
                  // min-h-0 so the reported-use step's scroll area has a bound
                  // to work against inside the fixed-height card — without it
                  // the list grew past the card and took CONFIRM off-screen.
                  //
                  // No vertical padding for the usage step: it fills the card
                  // and every pixel spent here is a pixel off the scrolling
                  // list. The short rejection views keep it — they are centred
                  // and would otherwise touch the card edges.
                  <div className={`flex-1 min-h-0 flex flex-col justify-center ${isUsageStep ? '' : 'py-6'}`}>
                    {scanIssueView}
                  </div>
                ) : (
                  <>
                    <HowItWorks className="hidden lg:block" />
                    <div className="flex gap-2.5 lg:gap-3 mt-3.5 lg:mt-[22px]">
                      <TipChip
                        icon={<LuSmile size={16} strokeWidth={1.5} />}
                        label="Neutral expression, even lighting"
                      />
                      <TipChip
                        icon={<LuCamera size={16} strokeWidth={1.5} />}
                        label="Front camera, arm’s length"
                      />
                    </div>
                    <div className="hidden lg:block lg:flex-1" />
                  </>
                )}
                {/* SUPPRESSED DURING THE REPORTED-USE STEP. It was a second
                    primary button under a card that already had one, and
                    pressing it did not capture anything — `usageCheck` had no
                    case in handleCapture, so it fell through to resetScan() and
                    threw the staged portrait away. Hiding it also hands its
                    ~90px back to the list, which is the point: the questions
                    are what the user is here to answer. */}
                {!isUsageStep && (
                  <>
                    <CaptureButton
                      state={captureBtnState}
                      onClick={handleCapture}
                      className="mt-4 lg:mt-7"
                    />
                    <p className="font-display text-center text-xs lg:text-sm text-bone-dim mt-3 lg:mt-3.5 m-0">
                      Your scan is stored privately in your Vault.
                    </p>
                  </>
                )}
              </div>
            </div>
            )
          ) : (
            /* ---------- GLOWUP BUILDER ---------- */
            <div className="mt-5 lg:mt-[34px] flex flex-col lg:flex-row gap-5 lg:gap-9 lg:items-start">
              <div className="flex-1 min-w-0 bg-[#1B1B1A] border border-hairline-soft rounded-card p-[18px] lg:p-[26px]">
                {archive.phase === 'loading' || archive.phase === 'idle' ? (
                  <div className="py-16 flex justify-center">
                    <LogoSpinner />
                  </div>
                ) : archive.phase === 'error' ? (
                  <div className="py-10 flex justify-center">
                    <EmptyState
                      headline="THE ARCHIVE IS UNREACHABLE"
                      subtitle={archive.message}
                      ctaLabel={COPY.TRY_AGAIN_CTA}
                      onCta={() => setArchive({ phase: 'idle' })}
                    />
                  </div>
                ) : entries.length < 2 ? (
                  <div className="py-10 flex justify-center">
                    <EmptyState
                      headline="YOUR ARCHIVE NEEDS TWO SCANS"
                      subtitle="Capture at least two portraits before composing a glowup."
                      ctaLabel="CAPTURE A SCAN"
                      onCta={() => setMode('scan')}
                      icon={<LuScanFace size={22} />}
                    />
                  </div>
                ) : (
                  <>
                    {/* heading — mobile only; on desktop it lives in the publish
                        rail to the right (see the aside below), mirroring the
                        scan screen's visual-left / info-right split. */}
                    <div className="lg:hidden">
                      <div className="font-display uppercase text-xs tracking-[0.24em] text-[#E8E2D6]">
                        COMPOSE YOUR GLOWUP
                      </div>
                      <p className="font-display italic text-[13px] text-bone-dim mt-1 m-0">
                        Pick a before and an after from your archive. Selected slot is highlighted.
                      </p>
                    </div>

                    {/* the captions + upload CTAs sit in a separate row below the
                        frames so the arrow can center on the FRAME band, not the
                        whole slot. Frames take an explicit, viewport-relative
                        height (see GlowupSlot) — like the scan viewport — so the
                        production CSS build can't collapse them. */}
                    <div className="mt-3.5 lg:mt-0">
                      <div className="relative grid grid-cols-2 gap-3 lg:gap-[18px]">
                        <GlowupSlot
                          label="BEFORE"
                          entry={beforeEntry}
                          freshUpload={beforeFresh}
                          active={slots.active === 'before'}
                          onActivate={() => setSlots((s) => ({ ...s, active: 'before' }))}
                        />
                        <GlowupSlot
                          label="AFTER"
                          entry={afterEntry}
                          freshUpload={afterFresh}
                          active={slots.active === 'after'}
                          onActivate={() => setSlots((s) => ({ ...s, active: 'after' }))}
                        />
                        {/* arrow medallion — centered on the frame band (the label
                            sits ~24px above the frame, so nudge down ~half that) */}
                        <div
                          aria-hidden="true"
                          className="absolute left-1/2 top-[calc(50%+11px)] -translate-x-1/2 -translate-y-1/2 w-[46px] h-[46px] rounded-full bg-vein border border-[rgba(255,255,255,0.18)] flex items-center justify-center pointer-events-none"
                          style={{ boxShadow: '0 8px 20px rgba(0,0,0,0.6)' }}
                        >
                          <LuArrowRight size={18} color="#FFFFFF" strokeWidth={1.5} />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 lg:gap-[18px] mt-2.5">
                        <GlowupSlotFooter
                          label="BEFORE"
                          entry={beforeEntry}
                          freshUpload={beforeFresh}
                          onUpload={() => triggerFreshUpload('before')}
                          onSetDate={handleSetDate}
                        />
                        <GlowupSlotFooter
                          label="AFTER"
                          entry={afterEntry}
                          freshUpload={afterFresh}
                          onUpload={() => triggerFreshUpload('after')}
                          onSetDate={handleSetDate}
                        />
                      </div>
                    </div>

                    {/* shared file picker for the per-slot "or upload" links */}
                    <input
                      ref={glowupUploadRef}
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(e) => {
                        handleFreshUpload(Array.from(e.target.files || []));
                        e.target.value = '';
                      }}
                    />

                    {/* mobile delta line */}
                    <div className="lg:hidden flex items-baseline justify-center gap-2 mt-4">
                      <span className="font-display font-medium text-[40px] leading-none" style={DELTA_GRADIENT_STYLE}>
                        {deltaLabel}
                      </span>
                      <span className="font-display text-xs text-bone-dim">points improved</span>
                    </div>

                    <div className="mt-4 lg:mt-6 lg:shrink-0 lg:border-t lg:border-[rgba(255,255,255,0.07)] lg:pt-[18px]">
                      <div className="font-display uppercase text-[11px] tracking-[0.22em] text-bone-dim">
                        YOUR ARCHIVE
                      </div>
                      <ArchiveStrip
                        entries={entries}
                        beforeId={slots.before}
                        afterId={slots.after}
                        onPick={handlePick}
                      />
                    </div>

                    {/* mobile publish */}
                    <div className="lg:hidden pt-3.5 pb-1">
                      <PublishButton state={publishState} onClick={handlePublish} />
                      {publishError}
                    </div>
                  </>
                )}
              </div>

              {/* publish rail (desktop) */}
              {archive.phase === 'ready' && entries.length >= 2 && (
                <aside className="hidden lg:block w-[320px] shrink-0 bg-[rgba(38,38,37,0.55)] border border-[rgba(255,255,255,0.10)] rounded-card p-[22px]">
                  <div className="font-display uppercase text-xs tracking-[0.24em] text-[#E8E2D6]">
                    COMPOSE YOUR GLOWUP
                  </div>
                  <p className="font-display italic text-base text-bone-dim mt-1.5 mb-[22px] pb-[18px] border-b border-[rgba(255,255,255,0.07)]">
                    Pick a before and an after from your archive. Selected slot is highlighted.
                  </p>
                  <div className="font-display uppercase text-[13px] tracking-[0.2em] text-bone">
                    PUBLISH TO COMMUNITY
                  </div>
                  <p className="font-display text-[14.5px] leading-normal text-bone-dim mt-2.5 mb-[18px]">
                    Share your progress anonymously and collect encouragement from the community.
                  </p>

                  <div className="bg-[#1B1B1A] border border-hairline-soft rounded-panel p-[18px] text-center">
                    <div className="font-display uppercase text-[11px] tracking-[0.2em] text-bone-dim">
                      PROGRESS
                    </div>
                    <div className="flex items-baseline justify-center gap-2 mt-2">
                      <span
                        className="font-display font-medium text-[54px] leading-none"
                        style={DELTA_GRADIENT_STYLE}
                      >
                        {deltaLabel}
                      </span>
                      {bothFilled && (
                        <LuTrendingUp size={20} color="#C9A45C" strokeWidth={1.8} aria-hidden="true" />
                      )}
                    </div>
                    <div className="font-display text-[13px] text-bone-dim mt-1.5">
                      points improved
                    </div>
                  </div>

                  <PublishButton
                    state={publishState}
                    onClick={handlePublish}
                    className="mt-[18px]"
                  />
                  {publishError}
                </aside>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile photo-picker sheet (posing prompts + native picker). Mounts the
          dropzone input so open() is wired while it is up. */}
      <ScanPhotoPickerModal
        open={isMobile && mode === 'scan' && pickerOpen}
        onChoose={open}
        onClose={() => setPickerOpen(false)}
        inputProps={getInputProps()}
      />

      <ScanRevealModal
        open={revealOpen}
        score10={revealScore10}
        percentileTopText={revealPercentileText}
        deltaText={scanDelta ? scanDelta.label : null}
        deltaDirection={scanDelta ? scanDelta.direction : 'up'}
        onViewScore={() => navigate(vaultEntryPath(scan.result.scan_id))}
        onReviewRoutine={() => navigate(ROUTES.ROUTINE)}
        onDismiss={() => (isMobile ? resetScan() : setRevealDismissed(true))}
      />

      {/* One modal serves both sealed states. `locked` is THIS scan sealed on
          arrival; `pendingUnlock` is a PREVIOUS scan still sealed, which is why
          the second carries no scan id and overrides the copy — telling someone
          "ten scans to open this" about a scan that was refused before it ran
          would be a lie. The decision offered is identical either way: judge,
          or start a trial. */}
      <UnlockGateModal
        open={scan.phase === 'locked' || scan.phase === 'pendingUnlock'}
        surface={scan.phase === 'pendingUnlock' ? 'create_pending_unlock' : 'create'}
        unlock={scan.unlock}
        headline={scan.phase === 'pendingUnlock'
          ? COPY.SCAN_PENDING_UNLOCK_HEADLINE
          : undefined}
        subcopy={scan.phase === 'pendingUnlock'
          ? COPY.SCAN_PENDING_UNLOCK_BODY
          : undefined}
        onJudge={() => navigate(scan.scanId
          ? `${ROUTES.JUDGE}?unlock=${scan.scanId}`
          : ROUTES.JUDGE)}
        onDismiss={() => navigate(ROUTES.HOME)}
      />
    </AppLayout>
  );
};

export default Create;
